import { applyLocalDateTime } from './calculator.js';
import { resolveDaysOfMonthFromConfig, resolveMonthDaysFromRule } from './month-days.js';
import { previewNextRuns } from './preview.js';
import { normalizeRecurrenceRule, parseIsoDateString, toIsoDateString, } from './recurrence-rule.js';
function resolveEnds(endDate, maxOccurrences) {
    if (maxOccurrences !== undefined && maxOccurrences > 0) {
        return 'after_count';
    }
    if (endDate) {
        return 'on_date';
    }
    return 'never';
}
export function fromLegacySchedule(fields) {
    const { scheduleType, scheduleConfig, startDate, endDate, timezone } = fields;
    const ends = resolveEnds(endDate, scheduleConfig.maxOccurrences);
    const base = {
        type: 'daily',
        startDate: toIsoDateString(startDate),
        time: scheduleConfig.time ?? '09:00',
        timezone,
        ends,
        ...(ends === 'on_date' && endDate ? { endDate: toIsoDateString(endDate) } : {}),
        ...(ends === 'after_count' && scheduleConfig.maxOccurrences
            ? { occurrences: scheduleConfig.maxOccurrences }
            : {}),
    };
    switch (scheduleType) {
        case 'once':
            return normalizeRecurrenceRule({ ...base, type: 'once' });
        case 'daily':
            return normalizeRecurrenceRule({ ...base, type: 'daily' });
        case 'weekly':
            return normalizeRecurrenceRule({
                ...base,
                type: 'weekly',
                weekdays: scheduleConfig.daysOfWeek ?? [1, 2, 3, 4, 5],
            });
        case 'monthly':
            return normalizeRecurrenceRule({
                ...base,
                type: 'monthly',
                monthDays: resolveDaysOfMonthFromConfig(scheduleConfig),
            });
        case 'yearly':
            return normalizeRecurrenceRule({
                ...base,
                type: 'yearly',
                yearDate: {
                    month: scheduleConfig.month ?? 1,
                    day: scheduleConfig.day ?? 1,
                },
            });
        case 'bimonthly':
            return normalizeRecurrenceRule({
                ...base,
                type: 'interval',
                interval: 2,
                intervalUnit: 'month',
                monthDay: scheduleConfig.dayOfMonth ?? 1,
                startDate: anchorStartDate(startDate, scheduleConfig.month),
            });
        case 'quarterly':
            return normalizeRecurrenceRule({
                ...base,
                type: 'interval',
                interval: 3,
                intervalUnit: 'month',
                monthDay: scheduleConfig.dayOfMonth ?? 1,
                startDate: anchorStartDate(startDate, scheduleConfig.month),
            });
        case 'semiannual':
            return normalizeRecurrenceRule({
                ...base,
                type: 'interval',
                interval: 6,
                intervalUnit: 'month',
                monthDay: scheduleConfig.dayOfMonth ?? 1,
                startDate: anchorStartDate(startDate, scheduleConfig.month),
            });
        case 'interval': {
            if (scheduleConfig.intervalUnit && scheduleConfig.intervalValue) {
                return normalizeRecurrenceRule({
                    ...base,
                    type: 'interval',
                    interval: scheduleConfig.intervalValue,
                    intervalUnit: scheduleConfig.intervalUnit,
                    startDate: toIsoDateString(startDate),
                    ...(scheduleConfig.intervalUnit === 'month'
                        ? { monthDays: resolveDaysOfMonthFromConfig(scheduleConfig) }
                        : {}),
                });
            }
            const intervalDays = scheduleConfig.intervalDays ?? 7;
            if (intervalDays % 7 === 0 && intervalDays >= 7) {
                return normalizeRecurrenceRule({
                    ...base,
                    type: 'interval',
                    interval: intervalDays / 7,
                    intervalUnit: 'week',
                    startDate: toIsoDateString(startDate),
                });
            }
            return normalizeRecurrenceRule({
                ...base,
                type: 'interval',
                interval: intervalDays,
                intervalUnit: 'day',
                startDate: toIsoDateString(startDate),
            });
        }
        default:
            return normalizeRecurrenceRule(base);
    }
}
function anchorStartDate(startDate, anchorMonth) {
    if (!anchorMonth) {
        return toIsoDateString(startDate);
    }
    const date = new Date(startDate);
    date.setMonth(anchorMonth - 1, 1);
    return toIsoDateString(date);
}
export function toLegacySchedule(rule) {
    const normalized = normalizeRecurrenceRule(rule);
    const startDate = normalized.startDate
        ? applyLocalDateTime(normalized.startDate, '00:00', normalized.timezone)
        : new Date();
    const scheduleConfig = {
        time: normalized.time ?? '09:00',
    };
    let scheduleType = 'daily';
    let endDate;
    let resolvedStartDate = startDate;
    if (normalized.ends === 'on_date' && normalized.endDate) {
        endDate = parseIsoDateString(normalized.endDate);
    }
    if (normalized.ends === 'after_count' && normalized.occurrences) {
        scheduleConfig.maxOccurrences = normalized.occurrences;
    }
    switch (normalized.type) {
        case 'once':
            scheduleType = 'once';
            break;
        case 'daily':
            scheduleType = 'daily';
            break;
        case 'weekly':
            scheduleType = 'weekly';
            scheduleConfig.daysOfWeek = normalized.weekdays ?? [1];
            break;
        case 'monthly': {
            scheduleType = 'monthly';
            const monthDays = resolveMonthDaysFromRule(normalized);
            scheduleConfig.daysOfMonth = monthDays;
            const singleDay = monthDays[0];
            if (monthDays.length === 1 && singleDay !== undefined) {
                scheduleConfig.dayOfMonth = singleDay;
            }
            break;
        }
        case 'yearly':
            scheduleType = 'yearly';
            scheduleConfig.month = normalized.yearDate?.month ?? 1;
            scheduleConfig.day = normalized.yearDate?.day ?? 1;
            break;
        case 'interval': {
            const interval = normalized.interval ?? 1;
            const unit = normalized.intervalUnit ?? 'day';
            if (unit === 'month') {
                const monthDays = resolveMonthDaysFromRule(normalized);
                scheduleConfig.daysOfMonth = monthDays;
                const singleDay = monthDays[0];
                if (monthDays.length === 1 && singleDay !== undefined) {
                    scheduleConfig.dayOfMonth = singleDay;
                }
                scheduleConfig.month =
                    (normalized.startDate ? parseIsoDateString(normalized.startDate) : startDate).getMonth() +
                        1;
                if (interval === 1) {
                    scheduleType = 'monthly';
                }
                else if (interval === 2) {
                    scheduleType = 'bimonthly';
                }
                else if (interval === 3) {
                    scheduleType = 'quarterly';
                }
                else if (interval === 6) {
                    scheduleType = 'semiannual';
                }
                else {
                    scheduleType = 'interval';
                    scheduleConfig.intervalUnit = 'month';
                    scheduleConfig.intervalValue = interval;
                }
                resolvedStartDate = startDate;
            }
            else if (unit === 'day') {
                scheduleType = 'interval';
                scheduleConfig.intervalDays = interval;
                resolvedStartDate = startDate;
            }
            else if (unit === 'week') {
                scheduleType = 'interval';
                scheduleConfig.intervalDays = interval * 7;
                resolvedStartDate = startDate;
            }
            else if (unit === 'year') {
                scheduleType = 'interval';
                scheduleConfig.intervalUnit = 'year';
                scheduleConfig.intervalValue = interval;
                resolvedStartDate = startDate;
            }
            else {
                scheduleType = 'interval';
                scheduleConfig.intervalUnit = unit;
                scheduleConfig.intervalValue = interval;
                resolvedStartDate = startDate;
            }
            break;
        }
        default:
            break;
    }
    return {
        scheduleType,
        scheduleConfig,
        startDate: resolvedStartDate,
        ...(endDate ? { endDate } : {}),
        timezone: normalized.timezone,
    };
}
export function intervalPresetMatches(rule, preset) {
    const normalized = normalizeRecurrenceRule(rule);
    return (normalized.type === 'interval' &&
        normalized.interval === preset.interval &&
        normalized.intervalUnit === preset.unit);
}
export function toLegacyScheduleForSave(rule) {
    const legacy = toLegacySchedule(rule);
    if (rule.ends === 'after_count' && rule.occurrences && rule.occurrences > 0) {
        legacy.scheduleConfig.maxOccurrences = rule.occurrences;
        const runs = previewNextRuns({
            scheduleType: legacy.scheduleType,
            scheduleConfig: legacy.scheduleConfig,
            timezone: legacy.timezone,
            startDate: legacy.startDate,
            count: rule.occurrences,
        });
        const lastRun = runs.at(-1);
        if (lastRun) {
            legacy.endDate = lastRun;
        }
    }
    return legacy;
}
//# sourceMappingURL=recurrence-adapter.js.map